/**
 * The line beside a greyed-out "Add AGG conform feedback" button, saying exactly why there is no
 * paragraph to add. The server names the reason (`feedbackBlockReason`); a draft without one comes
 * from before that field existed and keeps the old general sentence.
 */
import type { FeedbackBlockReason } from '../../types/taActionBoard';
import { EmailLibraryLink } from './EmailLibraryLink';

/** The Email Library template whose {feedback} paragraph words the block (REJECTION_TEMPLATE_KEY_BY_BRANCH.case). */
export const FEEDBACK_TEMPLATE_KEY = 'case-written-feedback';

export function AggFeedbackUnavailable({
  reason,
  language,
  topics,
}: {
  reason?: FeedbackBlockReason | null;
  language: 'de' | 'en';
  topics?: string[];
}) {
  const languageName = language === 'de' ? 'German' : 'English';
  switch (reason) {
    case 'empty-scorecard':
      return (
        <span>
          The interviewers did not write any feedback text in the scorecard (ratings only), so there
          is nothing to draft a paragraph from.
        </span>
      );
    case 'model-error':
      return (
        <span>
          The AI call that drafts the feedback paragraph failed, so no paragraph was written. You can
          still type the feedback into the email by hand.
        </span>
      );
    case 'nothing-safe':
      return topics?.length ? (
        <span>
          Left out: what the AI drafted from the interview feedback mentions{' '}
          {topics.join(' and ')}, which the AGG checks do not allow in a candidate email. You can
          still type the feedback by hand.
        </span>
      ) : (
        <span>
          What the AI drafted from the interview feedback did not pass the AGG checks (topic or
          language rules), so no paragraph was written. You can still type the feedback by hand.
        </span>
      );
    case 'model-generic':
      return (
        <span>
          The AI itself found nothing in the interview feedback that is safe to share (no word on the
          AGG lists was hit), so no paragraph was written. You can still type the feedback by hand.
        </span>
      );
    case 'wrong-language':
      return (
        <span>
          The AI kept writing the paragraph in the wrong language (not {languageName}), so none was
          added. You can still type the feedback by hand.
        </span>
      );
    case 'no-feedback-template':
      return (
        <span>
          A feedback paragraph was drafted, but the Email Library template that words it
          (“{FEEDBACK_TEMPLATE_KEY}”) is missing, switched off, empty in {languageName}, or no
          longer has a paragraph with {'{feedback}'} in it.{' '}
          <EmailLibraryLink templateKey={FEEDBACK_TEMPLATE_KEY} label="Open that template" />
        </span>
      );
    default:
      return <span>Nothing AGG-conform could be drafted from the interview feedback.</span>;
  }
}
